import React, { useEffect, useMemo, useState } from "react";
import { Download, Search, RefreshCw, FileImage, X, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fetchFileObjectUrl } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const STATUS_LABEL = { pending: "प्रलंबित", verified: "Verified", rejected: "Rejected" };
const REJECT_REASONS = ["UTR not found", "Amount mismatch", "Duplicate UTR", "Other"];

export default function FeePaymentsView({ mode = "accountant" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sub, setSub] = useState("pending"); // rdc sub-tabs
  const [busyId, setBusyId] = useState(null);
  const [proofUrl, setProofUrl] = useState("");
  const [proofType, setProofType] = useState("");
  const [rejectRow, setRejectRow] = useState(null);
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");

  const [stats, setStats] = useState(null);
  const loadStats = () => { if (mode !== "rdc") api.accountantPaymentStats().then(setStats).catch(() => {}); };
  const load = () => {
    setLoading(true);
    loadStats();
    (mode === "rdc" ? api.rdcPayments() : api.accountantPayments())
      .then(setRows).catch((e) => toast.error(e.message || "त्रुटी")).finally(() => setLoading(false));
  };
  useEffect(load, [mode]);

  const searched = useMemo(() => rows.filter((r) => {
    if (from && (!r.date || r.date.slice(0, 10) < from)) return false;
    if (to && (!r.date || r.date.slice(0, 10) > to)) return false;
    if (q) {
      const s = q.toLowerCase();
      if (![r.applicationNumber, r.name, r.utr].some((v) => (v || "").toLowerCase().includes(s))) return false;
    }
    return true;
  }), [rows, q, from, to]);

  const filtered = useMemo(() => {
    if (mode !== "rdc") return searched;
    if (sub === "duplicates") return searched.filter((r) => r.isDuplicate);
    return searched.filter((r) => r.paymentStatus === sub);
  }, [searched, mode, sub]);

  const cards = useMemo(() => {
    const cnt = (s) => rows.filter((r) => r.paymentStatus === s).length;
    const c = mode === "rdc" || !stats
      ? { total: rows.length, verified: cnt("verified"), rejected: cnt("rejected"), pending: cnt("pending") }
      : stats;
    return [["एकूण नोंदी", c.total], ["Verified", c.verified], ["Rejected", c.rejected], ["प्रलंबित (Pending)", c.pending]];
  }, [rows, mode, stats]);

  const openProof = async (r) => {
    if (!r.proofFileId) { toast.error("पुरावा उपलब्ध नाही"); return; }
    try {
      const { url, contentType } = await fetchFileObjectUrl(r.proofFileId);
      setProofType(contentType || ""); setProofUrl(url);
    } catch { toast.error("पुरावा उघडता आला नाही"); }
  };
  const closeProof = () => { if (proofUrl) URL.revokeObjectURL(proofUrl); setProofUrl(""); setProofType(""); };

  const verify = async (r) => {
    setBusyId(r.id);
    try { await api.accountantVerify(r.id, { status: "verified" }); toast.success("Verified"); setRows((p) => p.filter((x) => x.id !== r.id)); loadStats(); }
    catch (e) { toast.error(e.message || "त्रुटी"); }
    setBusyId(null);
  };

  const submitReject = async () => {
    if (!reason) { toast.error("कारण निवडा"); return; }
    if (reason === "Other" && !remarks.trim()) { toast.error("'Other' साठी टीप आवश्यक"); return; }
    setBusyId(rejectRow.id);
    try {
      await api.accountantVerify(rejectRow.id, { status: "rejected", reason, remarks });
      toast.success("Rejected"); setRows((p) => p.filter((x) => x.id !== rejectRow.id)); loadStats();
      setRejectRow(null); setReason(""); setRemarks("");
    } catch (e) { toast.error(e.message || "त्रुटी"); }
    setBusyId(null);
  };

  const sendReview = async (r) => {
    const note = window.prompt("पुनर्तपासणीसाठी टीप (ऐच्छिक):", "") ?? null;
    if (note === null) return;
    setBusyId(r.id);
    try { await api.rdcReviewPayment(r.id, note); toast.success("लेखापालाकडे पुनर्तपासणीसाठी पाठवले."); setRows((p) => p.map((x) => x.id === r.id ? { ...x, sentForReview: true } : x)); }
    catch (e) { toast.error(e.message || "त्रुटी"); }
    setBusyId(null);
  };

  const exportCsv = () => {
    const head = ["Sr No", "Date", "Application No", "Name", "UTR No", "Method", "Amount", "Status", "Duplicate", "Reason", "Remarks"];
    const lines = filtered.map((r, i) => [
      i + 1, fmtDate(r.date), r.applicationNumber, r.name, r.utr, r.method, r.amount,
      STATUS_LABEL[r.paymentStatus], r.isDuplicate ? "Yes" : "No", r.reason, r.remarks,
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [head.join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `payment-utr-register-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const cols = mode === "accountant" ? 9 : 8;

  return (
    <div data-testid="fee-payments-view">
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[22px] font-bold text-[#1b5e3f] font-latin">{v}</p>
            <p className="text-[12.5px] font-semibold text-slate-600">{l}</p>
          </div>
        ))}
      </div>

      {mode === "rdc" && (
        <div className="mb-3 flex flex-wrap gap-2">
          {[["verified", "Verified"], ["rejected", "Rejected"], ["pending", "Pending"], ["duplicates", "Duplicates"]].map(([k, l]) => (
            <button key={k} onClick={() => setSub(k)} data-testid={`fee-subtab-${k}`}
              className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${sub === k ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="अर्ज क्र./नाव/UTR शोधा" data-testid="fee-search"
            className="w-full rounded-full border border-slate-300 py-2 pl-9 pr-3 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
        </div>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="fee-from" className="rounded-full border border-slate-300 px-3 py-2 text-[13px]" title="पासून" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="fee-to" className="rounded-full border border-slate-300 px-3 py-2 text-[13px]" title="पर्यंत" />
        <button onClick={load} data-testid="fee-refresh" className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /></button>
        <button onClick={exportCsv} data-testid="fee-export" className="flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#144d33]"><Download className="h-4 w-4" /> Excel/CSV</button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-[#1b5e3f]/5 text-left text-[12px] uppercase text-slate-500">
            <tr>
              {[
                "अ.क्र.", "दिनांक", "अर्ज क्र.", "नाव",
                ...(mode === "accountant" ? ["पेमेंटची पद्धत"] : []),
                "UTR क्र.", "रक्कम", "पुरावा",
                mode === "rdc" ? "स्थिती / कृती" : "पडताळणी",
              ].map((h) => (
                <th key={h} className="whitespace-nowrap px-3 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols} className="py-10 text-center text-slate-400">लोड होत आहे...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={cols} className="py-10 text-center text-slate-400" data-testid="fee-empty">कोणतीही नोंद नाही</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={r.id} className="border-t border-slate-100 align-middle" data-testid={`payment-row-${r.applicationNumber}`}>
                <td className="px-3 py-2.5 text-slate-500">{i + 1}</td>
                <td className="whitespace-nowrap px-3 py-2.5">{fmtDate(r.date)}</td>
                <td className="px-3 py-2.5 font-semibold text-[#1b5e3f] font-latin">{r.applicationNumber}</td>
                <td className="px-3 py-2.5">{r.name || "—"}
                  {r.sentForReview && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700">पुनर्तपासणी</span>}
                </td>
                {mode === "accountant" && <td className="px-3 py-2.5" data-testid={`fee-method-${r.applicationNumber}`}>{r.method || "—"}</td>}
                <td className="px-3 py-2.5 font-latin">{r.utr || "—"}
                  {r.isDuplicate && <span data-testid={`fee-dup-badge-${r.applicationNumber}`} className="ml-1 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10.5px] font-bold text-red-600"><AlertTriangle className="h-3 w-3" />डुप्लिकेट</span>}
                </td>
                <td className="px-3 py-2.5 font-latin">₹{r.amount}</td>
                <td className="px-3 py-2.5">
                  <button onClick={() => openProof(r)} data-testid={`fee-proof-${r.applicationNumber}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#c2570a]/10 px-2.5 py-1 text-[12px] font-semibold text-[#c2570a] hover:bg-[#c2570a]/20">
                    <FileImage className="h-3.5 w-3.5" /> Proof
                  </button>
                </td>
                <td className="px-3 py-2.5">
                  {mode === "rdc" ? (
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold ${r.paymentStatus === "verified" ? "bg-emerald-100 text-emerald-700" : r.paymentStatus === "rejected" ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-500"}`}>{STATUS_LABEL[r.paymentStatus]}</span>
                      <button onClick={() => sendReview(r)} disabled={busyId === r.id} data-testid={`fee-sendreview-${r.applicationNumber}`}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-500 px-2.5 py-1 text-[11.5px] font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50"><Send className="h-3 w-3" /> पुनर्तपासणीसाठी पाठवा</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => verify(r)} disabled={busyId === r.id} data-testid={`fee-verify-${r.applicationNumber}`}
                        className="rounded-full bg-[#5a9e3a] px-3 py-1 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-50">Verify</button>
                      <button onClick={() => { setRejectRow(r); setReason(""); setRemarks(""); }} disabled={busyId === r.id} data-testid={`fee-reject-${r.applicationNumber}`}
                        className="rounded-full bg-red-600 px-3 py-1 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-50">Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {proofUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="fee-proof-modal" onClick={closeProof}>
          <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
              <p className="text-[14px] font-bold text-[#1b5e3f]">पेमेंट पुरावा</p>
              <div className="flex items-center gap-2">
                <a href={proofUrl} target="_blank" rel="noopener noreferrer" data-testid="fee-proof-newtab" className="rounded-full bg-[#1b5e3f]/10 px-3 py-1 text-[12.5px] font-semibold text-[#1b5e3f] hover:bg-[#1b5e3f]/20">नवीन टॅबमध्ये उघडा</a>
                <button onClick={closeProof} data-testid="fee-proof-close" className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="min-h-[60vh] overflow-auto bg-slate-50">
              {proofType.startsWith("image") ? (
                <img src={proofUrl} alt="payment proof" className="mx-auto max-h-[80vh] w-auto" />
              ) : (
                <iframe title="payment proof" src={proofUrl} className="h-[80vh] w-full border-0" />
              )}
            </div>
          </div>
        </div>
      )}

      {rejectRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="fee-reject-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-5">
            <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">पेमेंट नाकारा — अर्ज {rejectRow.applicationNumber}</p>
            <label className="mb-1.5 block text-[13px] font-semibold text-slate-600">कारण <span className="text-red-500">*</span></label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fee-reject-reason"
              className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]">
              <option value="">— कारण निवडा —</option>
              {REJECT_REASONS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            {reason === "Other" && (
              <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} data-testid="fee-reject-remarks"
                placeholder="टीप (आवश्यक)" className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setRejectRow(null)} className="rounded-full border border-slate-300 px-4 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">रद्द</button>
              <button onClick={submitReject} disabled={busyId === rejectRow.id} data-testid="fee-reject-confirm"
                className="rounded-full bg-red-600 px-5 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50">नाकारा</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
